<template>
  <View direction="column" gap="xl">
    <Text variant="body-medium" color="secondary" align="start">
      Select the tour options you want to offer to potential tenants. At least one option must be selected.
    </Text>

    <View direction="column" gap="md">
      <CheckboxCard
        title="In-person tour"
        description="Showcasing your property to tenants personally or by a leasing agent"
        icon="user-01"
        v-model="form.fields.allowsInPersonTours"
      />
      <CheckboxCard
        title="Virtual tour"
        description="Showcasing your property to tenants online via apps like FaceTime or Zoom"
        icon="video-recorder"
        v-model="form.fields.allowsVirtualTours"
      />
      <CheckboxCard
        title="Self-guided tour"
        description="Showcasing your property to tenants by providing them with instructions to access the property"
        icon="key"
        v-model="form.fields.allowsSelfGuidedTours"
      />

      <!-- Sits with the tour cards, not below the access form: it applies to every tour type the
           owner offers, so it is the decision made right after choosing how strangers get to see
           the place. A building sets the kinds only: ID checks and access are each unit's own. -->
      <IdentityVerificationOption v-if="hasTourType && !isBuilding" v-model="form.fields.identityVerificationEnabled" />
    </View>

    <SelfGuidedAccessForm
      ref="accessFormRef"
      v-if="form.fields.allowsSelfGuidedTours && !isBuilding"
      v-model="form.fields.selfGuidedAccessMethod"
      :property-id="propertyStore.property?.id"
    />
  </View>
</template>

<script setup lang="ts">
import { watch, computed, ref } from 'vue';

import { Text, View, CheckboxCard, useForm } from '@tallo/design-system';

import { useDetailsHolder } from '../../composables/details-holder';
import { SaveTourTypesDto } from '../../dtos/save-tour-types.dto.interface';
import { usePropertyStore } from '../../stores/property.store';
import IdentityVerificationOption from './identity-verification-option.vue';
import SelfGuidedAccessForm from './self-guided-access-form.vue';

const propertyStore = usePropertyStore();
// The page's holder: a unit's own tours, or the tours a building's new units start with
const holder = useDetailsHolder();
const isBuilding = holder.kind === 'building';

const atLeastOneTourType = (value: boolean, siblings: SaveTourTypesDto) => {
  return value || siblings.allowsInPersonTours || siblings.allowsVirtualTours || siblings.allowsSelfGuidedTours;
};

const form = useForm<SaveTourTypesDto>(
  {
    allowsInPersonTours: true,
    allowsVirtualTours: true,
    allowsSelfGuidedTours: true,
    selfGuidedAccessMethod: null,
    identityVerificationEnabled: false,
  },
  {
    allowsInPersonTours: { atLeastOneTourType },
    allowsVirtualTours: { atLeastOneTourType },
    allowsSelfGuidedTours: { atLeastOneTourType },
  },
);

const hasTourType = computed(
  () => form.fields.allowsInPersonTours || form.fields.allowsVirtualTours || form.fields.allowsSelfGuidedTours,
);

const isFormValid = computed(() => {
  if (!hasTourType.value) return false;

  return form.fields.allowsSelfGuidedTours && !isBuilding ? Boolean(accessFormRef.value?.isValid) : true;
});

function populateForm() {
  const property = propertyStore.property;
  // A building's defaults are one object; unset, the sheet opens with every kind on, as a new home's does
  const tours = isBuilding ? holder.details?.tourTypes : property;
  Object.assign(form.fields, {
    // If value is null, default to false; otherwise use the existing value
    allowsInPersonTours: isBuilding && !tours ? true : (tours?.allowsInPersonTours ?? false),
    allowsVirtualTours: isBuilding && !tours ? true : (tours?.allowsVirtualTours ?? false),
    allowsSelfGuidedTours: isBuilding && !tours ? true : (tours?.allowsSelfGuidedTours ?? false),
    selfGuidedAccessMethod: property?.selfGuidedAccessMethod ?? null,
    identityVerificationEnabled: property?.identityVerificationEnabled ?? false,
  });
}

const accessFormRef = ref<InstanceType<typeof SelfGuidedAccessForm> | null>(null);

async function save() {
  return form.submit(async (fields) => {
    await holder.saveTourTypes(fields);
    await accessFormRef.value?.save();
  });
}

watch(() => (isBuilding ? holder.details?.tourTypes : propertyStore.property), populateForm, { immediate: true });

defineExpose({ save, form, isFormValid });
</script>
